import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL_AUTH } from '../config/api';
import COUNTRY_CODES, { DEFAULT_COUNTRY_CODE } from '../utils/countryCodes';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const OTP_RESEND_COOLDOWN = 60;

function validateEmail(email) {
  if (!email) return '';
  if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address.';
  return '';
}

function validatePhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return 'Phone number is too short.';
  if (digits.length > 15) return 'Phone number is too long.';
  return '';
}

function getFlagEmoji(countryCode) {
  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch {
    return '';
  }
}

const STEPS = { REGISTER: 0, OTP: 1 };

export default function RegisterPage() {
  const [step, setStep] = useState(STEPS.REGISTER);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [generation, setGeneration] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const formTokenRef = useRef(Math.floor(Date.now() / 1000));
  const { login } = useAuth();
  const navigate = useNavigate();

  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [otp, setOtp] = useState('');
  const [verifyVia, setVerifyVia] = useState('');
  const [verifyIdentifier, setVerifyIdentifier] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);

  const selectedCountry = useMemo(
    () => COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0],
    [countryCode]
  );

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return COUNTRY_CODES;
    const q = countrySearch.toLowerCase();
    return COUNTRY_CODES.filter(
      c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [countrySearch]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (document.getElementById('recaptcha-v3-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const getRecaptchaToken = () => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return Promise.resolve('');
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: 'register' })
          .then(resolve)
          .catch(() => resolve(''));
      });
    });
  };

  const emailError = emailTouched ? validateEmail(email) : '';
  const phoneError = phoneTouched ? validatePhone(phone) : '';

  const parseApiError = (data) => {
    if (data.errors) {
      if (Array.isArray(data.errors)) {
        return data.errors.map(err => err.message).join(' ');
      }
      return Object.values(data.errors).flat().join(' ');
    }
    return data.message || 'Something went wrong. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailErr = validateEmail(email);
    const phoneErr = validatePhone(phone);

    if (!name || !password || !confirmPassword || !generation) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!phone) {
      setError('Please provide a phone number.');
      return;
    }
    if (phone && phoneErr) {
      setError(phoneErr);
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const recaptchaToken = await getRecaptchaToken();
      const payload = {
        name,
        password,
        generation,
        website_url: honeypot,
        _form_token: formTokenRef.current,
        country_code: selectedCountry.code,
      };
      if (email) payload.email = email;
      if (phone) {
        const digits = phone.replace(/\D/g, '');
        payload.phone = selectedCountry.phoneCode + digits;
      }
      if (recaptchaToken) payload.recaptcha_token = recaptchaToken;

      const response = await fetch(`${API_BASE_URL_AUTH}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === 1) {
        login(data.token, data.user_data);
        navigate('/search', { state: { welcome: true } });
      } else if (response.ok && data.requires_otp) {
        setVerifyVia(data.verify_via);
        setVerifyIdentifier(data.verify_identifier);
        setStep(STEPS.OTP);
        setResendCooldown(OTP_RESEND_COOLDOWN);
        setSuccess(data.message);
        setError('');
      } else {
        setError(parseApiError(data));
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An error occurred during registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.replace(/\D/g, '');
    if (otpValue.length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const payload = { otp: otpValue };
      if (verifyVia === 'email') payload.email = verifyIdentifier;
      else payload.phone = verifyIdentifier;

      const response = await fetch(`${API_BASE_URL_AUTH}/auth/verify-registration-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.status === 1) {
        login(data.token, data.user_data);
        navigate('/search', { state: { welcome: true, welcomePoints: data.welcome_points } });
      } else {
        setError(parseApiError(data));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const payload = {};
      if (verifyVia === 'email') payload.email = verifyIdentifier;
      else payload.phone = verifyIdentifier;

      const response = await fetch(`${API_BASE_URL_AUTH}/auth/resend-registration-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('A new verification code has been sent.');
        setResendCooldown(OTP_RESEND_COOLDOWN);
        setOtp('');
      } else {
        setError(parseApiError(data));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newOtp = otp.split('');
    while (newOtp.length < 6) newOtp.push('');
    newOtp[index] = value;
    setOtp(newOtp.join(''));

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste) {
      setOtp(paste.padEnd(6, '').slice(0, 6));
      const focusIdx = Math.min(paste.length, 5);
      otpInputRefs.current[focusIdx]?.focus();
    }
  };

  const maskedIdentifier = verifyVia === 'email'
    ? verifyIdentifier.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(b.length) + c)
    : verifyIdentifier.replace(/^(.{4})(.*)(.{2})$/, (_, a, b, c) => a + '*'.repeat(b.length) + c);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-800 to-red-600">
      {step === STEPS.REGISTER && (
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center text-red-800">Create Account</h2>
          {error && <div className="mb-4 text-red-500 text-sm p-2 bg-red-100 rounded">{error}</div>}

          {/* Honeypot */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="website_url">Website</label>
            <input
              id="website_url"
              name="website_url"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-1" htmlFor="name">Full Name <span className="text-red-500">*</span></label>
            <input
              id="name"
              type="text"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="mb-1 p-3 bg-gray-50 rounded border border-gray-200">

            <div>
              <label className="block text-gray-700 mb-1" htmlFor="phone">Phone Number</label>
              <div className="flex gap-0">
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                    className="flex items-center gap-1 px-2 py-2 border border-r-0 rounded-l bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap h-[42px] min-w-[90px] justify-center focus:outline-none focus:ring-2 focus:ring-red-400"
                    disabled={loading}
                  >
                    <span className="text-base">{getFlagEmoji(selectedCountry.code)}</span>
                    <span>+{selectedCountry.phoneCode}</span>
                    <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {countryDropdownOpen && (
                    <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-white border rounded-lg shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b sticky top-0 bg-white">
                        <input
                          type="text"
                          placeholder="Search country..."
                          className="w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                          value={countrySearch}
                          onChange={e => setCountrySearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredCountries.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-400">No results found</div>
                        ) : (
                          filteredCountries.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 ${
                                c.code === countryCode ? 'bg-red-100 font-medium' : ''
                              }`}
                              onClick={() => {
                                setCountryCode(c.code);
                                setCountryDropdownOpen(false);
                                setCountrySearch('');
                              }}
                            >
                              <span className="text-base">{getFlagEmoji(c.code)}</span>
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="text-gray-400">+{c.phoneCode}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <input
                  id="phone"
                  type="tel"
                  placeholder="e.g. 1234567"
                  className={`flex-1 px-3 py-2 border rounded-r focus:outline-none focus:ring-2 ${
                    phoneError ? 'border-red-400 focus:ring-red-300' : 'focus:ring-red-400'
                  }`}
                  value={phone}
                  onChange={e => {
                    const val = e.target.value.replace(/[^\d\s\-()]/g, '');
                    setPhone(val);
                  }}
                  onBlur={() => setPhoneTouched(true)}
                  disabled={loading}
                />
              </div>
              {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
              {phone && !phoneError && phoneTouched && (
                <p className="text-gray-400 text-xs mt-1">
                  Full number: +{selectedCountry.phoneCode} {phone}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4 mt-4">
            <label className="block text-gray-700 mb-1" htmlFor="generation">Generation <span className="text-red-500">*</span></label>
            <select
              id="generation"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
              value={generation}
              onChange={e => setGeneration(e.target.value)}
              disabled={loading}
            >
              <option value="">Select your generation</option>
              <option value="gen_z">Gen Z (Born 1997-2012)</option>
              <option value="millennial">Millennial (Born 1981-1996)</option>
              <option value="gen_x">Gen X (Born 1965-1980)</option>
              <option value="baby_boomer">Baby Boomer (Born 1946-1964)</option>
              <option value="silent_generation">Silent Generation (Born 1928-1945)</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-1" htmlFor="password">Password <span className="text-red-500">*</span></label>
            <input
              id="password"
              type="password"
              placeholder="Min. 8 characters"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 mb-1" htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></label>
            <input
              id="confirmPassword"
              type="password"
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <button type="submit" className="w-full bg-red-800 text-white py-2 rounded hover:bg-red-900 transition" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>

          <p className="text-center text-sm text-gray-600 mt-4">
            Already have an account? <Link to="/login" className="text-red-800 hover:underline">Login here</Link>
          </p>
        </form>
      )}

      {step === STEPS.OTP && (
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
          <h2 className="text-2xl font-bold mb-2 text-center text-red-800">Verify Your Account</h2>

          <div className="flex justify-center gap-2 mb-4">
            <div className="h-1.5 rounded-full bg-red-800 w-10 transition-all duration-300" />
            <div className="h-1.5 rounded-full bg-red-800 w-10 transition-all duration-300" />
          </div>

          {error && <div className="mb-4 text-red-500 text-sm p-2 bg-red-100 rounded">{error}</div>}
          {success && <div className="mb-4 text-green-700 text-sm p-2 bg-green-100 rounded">{success}</div>}

          <p className="text-gray-600 text-sm mb-6 text-center">
            We sent a 6-digit code to{' '}
            <span className="font-medium text-gray-800">{maskedIdentifier}</span>.
            {verifyVia === 'email' ? ' Check your email inbox (and spam folder).' : ''}
          </p>

          <form onSubmit={handleVerifyOtp}>
            <div className="flex justify-center gap-2 mb-6" onPaste={handleOtpPaste}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  ref={el => otpInputRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="w-11 h-12 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  value={otp[i] || ''}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  disabled={loading}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <button
              type="submit"
              className="w-full bg-red-800 text-white py-2.5 rounded hover:bg-red-900 transition disabled:opacity-60 font-medium"
              disabled={loading || otp.replace(/\D/g, '').length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify & Complete Registration'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendCooldown > 0 || loading}
              className={`text-sm ${
                resendCooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-red-800 hover:underline'
              }`}
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : "Didn't receive the code? Resend"}
            </button>
          </div>

          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => {
                setStep(STEPS.REGISTER);
                setOtp('');
                setError('');
                setSuccess('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Back to registration
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
